import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Select,
  message,
  Switch,
  Collapse,
  Table,
  Alert,
  Tooltip,
} from "antd";
import {
  InfoCircleOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Booking, Product, DeliveryInfo } from "../../../types/booking";
import {
  calculateLmdCost,
  getLmdCountries,
  getDieselSurchargePercentage,
  getCartonsPerPallet,
  getPalletThreshold,
  getLmdRates,
  type LmdCalculation,
  type LmdRate,
} from "../../../services/lmdRatesService";

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

interface LMDSectionProps {
  selectedBooking: Booking | null;
  onAddLMDLine: (line: {
    description: string;
    quantity: number;
    unit_price: number;
    vat_percentage: number;
    cost_type: string;
  }) => void;
}

const LMDSection: React.FC<LMDSectionProps> = ({
  selectedBooking,
  onAddLMDLine,
}) => {
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [calculation, setCalculation] = useState<LmdCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableRates, setAvailableRates] = useState<LmdRate[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showLmdOverride, setShowLmdOverride] = useState(false);
  const [hasDeliveryAddress, setHasDeliveryAddress] = useState<boolean>(false);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);

  const [lmdValues, setLmdValues] = useState({
    vat_percentage: 21,
    delivery_type: "auto" as "auto" | "manual" | "carton",
    apply_diesel_surcharge: true,
    diesel_surcharge_percentage: 15,
    country: "NL",
    // Manual override fields
    manual_pallets: 1,
    manual_base_rate: 0,
    manual_total_cost: 0,
    // Carton shipment override
    carton_shipment_cartons: 0,
  });

  // Check if booking has delivery address
  useEffect(() => {
    if (!selectedBooking) {
      setHasDeliveryAddress(false);
      setDeliveryInfo(null);
      setShowLmdOverride(false);
      return;
    }

    // Check booking_delivery_info for delivery address
    let delivery: DeliveryInfo | null = null;
    let hasDelivery = false;

    if (selectedBooking.booking_delivery_info) {
      // Handle both array and single object cases
      if (Array.isArray(selectedBooking.booking_delivery_info)) {
        if (selectedBooking.booking_delivery_info.length > 0) {
          delivery = selectedBooking.booking_delivery_info[0];
          hasDelivery = true;
        }
      } else {
        delivery = selectedBooking.booking_delivery_info;
        hasDelivery = true;
      }
    }

    setHasDeliveryAddress(hasDelivery);
    setDeliveryInfo(delivery);

    // Only show LMD automatically if there's a delivery address
    if (!hasDelivery) {
      setShowLmdOverride(false);
    }
  }, [selectedBooking]);

  // Load available countries and settings
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setValidationErrors([]);
        const [countries, dieselPercentage] = await Promise.all([
          getLmdCountries(),
          getDieselSurchargePercentage(),
        ]);

        if (countries.length === 0) {
          setValidationErrors([
            "No LMD countries available. Please contact administrator.",
          ]);
          return;
        }

        setAvailableCountries(countries);

        // Update state with loaded settings
        setLmdValues((prev) => ({
          ...prev,
          country:
            countries.length > 0 && !countries.includes(prev.country)
              ? countries[0]
              : prev.country,
          diesel_surcharge_percentage: dieselPercentage,
        }));
      } catch (error) {
        console.error("Error loading LMD data:", error);
        const errorMsg =
          error instanceof Error ? error.message : "Unknown error";
        setValidationErrors([`Failed to load LMD configuration: ${errorMsg}`]);
        message.error("Failed to load LMD configuration");
      }
    };
    loadInitialData();
  }, []);

  // Load rates for selected country
  useEffect(() => {
    const loadRatesForCountry = async () => {
      if (!lmdValues.country) return;

      setRatesLoading(true);
      try {
        const rates = await getLmdRates(lmdValues.country, "ex_diesel");
        setAvailableRates(rates);
      } catch (error) {
        console.error("Error loading rates for country:", error);
        setAvailableRates([]);
      } finally {
        setRatesLoading(false);
      }
    };

    loadRatesForCountry();
  }, [lmdValues.country]);

  // Helper to get booking products in consistent format
  const getBookingProducts = (booking: Booking): Product[] => {
    if (!booking.product_info) return [];

    // Handle different structures of product_info from booking_product table
    if (Array.isArray(booking.product_info)) {
      // Already an array of products
      return booking.product_info as Product[];
    }

    // Single product
    return [booking.product_info as Product];
  };

  // Calculate total cartons with smart priority
  const getTotalCartonsFromBooking = (): number => {
    if (!selectedBooking) return 0;

    // Priority 1: Use delivery_cartons from specific delivery
    const deliveryInfo = Array.isArray(selectedBooking.booking_delivery_info)
      ? selectedBooking.booking_delivery_info[0]
      : selectedBooking.booking_delivery_info;

    if (deliveryInfo?.delivery_cartons && deliveryInfo.delivery_cartons > 0) {
      return deliveryInfo.delivery_cartons;
    }

    // Priority 2: Use total_actual_cartons from container unloading
    if (
      selectedBooking.total_actual_cartons &&
      selectedBooking.total_actual_cartons > 0
    ) {
      return selectedBooking.total_actual_cartons;
    }

    // Priority 3: Fallback to estimated cartons from product_info
    if (!selectedBooking.product_info) return 0;

    const products = getBookingProducts(selectedBooking);
    return products.reduce((total, product) => {
      return total + (product?.cartons || 0);
    }, 0);
  };

  // Auto-recalculate in manual mode when pallets change
  const handleManualPalletChange = async (pallets: number) => {
    setLmdValues((prev) => ({ ...prev, manual_pallets: pallets }));

    // Try to find the rate for this pallet count and update base rate
    if (pallets > 0 && availableRates.length > 0) {
      try {
        // Find rate for this pallet count (max 15 for lookup)
        const lookupPallets = Math.min(pallets, 15);
        const rate = availableRates.find((r) => r.pallets === lookupPallets);

        if (rate) {
          const newBaseRate = Number(rate.rate_eur);
          const dieselSurcharge = lmdValues.apply_diesel_surcharge
            ? (newBaseRate * lmdValues.diesel_surcharge_percentage) / 100
            : 0;
          const newTotalCost = newBaseRate + dieselSurcharge;

          setLmdValues((prev) => ({
            ...prev,
            manual_base_rate: Number(newBaseRate.toFixed(2)),
            manual_total_cost: Number(newTotalCost.toFixed(2)),
          }));
        }
      } catch (error) {
        console.error("Error auto-updating rates:", error);
      }
    }
  };

  // Handle manual base rate change - recalculate total
  const handleManualBaseRateChange = (baseRate: number) => {
    const dieselSurcharge = lmdValues.apply_diesel_surcharge
      ? (baseRate * lmdValues.diesel_surcharge_percentage) / 100
      : 0;
    const newTotalCost = baseRate + dieselSurcharge;

    setLmdValues((prev) => ({
      ...prev,
      manual_base_rate: baseRate,
      manual_total_cost: Number(newTotalCost.toFixed(2)),
    }));
  };

  // Enhanced validation
  const validateLmdInputs = (): string[] => {
    const errors: string[] = [];

    if (!selectedBooking) {
      errors.push("Please select a booking first");
    }

    if (!lmdValues.country) {
      errors.push("Please select a delivery country");
    }

    if (
      (lmdValues.delivery_type === "auto" ||
        lmdValues.delivery_type === "carton") &&
      availableRates.length === 0
    ) {
      errors.push(`No rates available for ${lmdValues.country}`);
    }

    const totalCartons = getTotalCartonsFromBooking();
    if (
      (lmdValues.delivery_type === "auto" ||
        lmdValues.delivery_type === "carton") &&
      totalCartons === 0
    ) {
      errors.push("Booking has no cartons. Please check product information.");
    }

    if (lmdValues.delivery_type === "manual") {
      if (lmdValues.manual_pallets < 1) {
        errors.push("Manual pallet count must be at least 1");
      }
      if (lmdValues.manual_total_cost <= 0) {
        errors.push("Manual total cost must be greater than 0");
      }
    }

    if (lmdValues.vat_percentage < 0 || lmdValues.vat_percentage > 100) {
      errors.push("VAT percentage must be between 0 and 100");
    }

    return errors;
  };

  // Calculate LMD cost using database service
  const performLmdCalculation = async (): Promise<void> => {
    if (!selectedBooking) return;

    const errors = validateLmdInputs();
    setValidationErrors(errors);

    if (errors.length > 0) {
      setCalculation(null);
      return;
    }

    setLoading(true);
    try {
      if (lmdValues.delivery_type === "manual") {
        // Use manual values directly
        const dieselSurcharge = lmdValues.apply_diesel_surcharge
          ? (lmdValues.manual_base_rate *
              lmdValues.diesel_surcharge_percentage) /
            100
          : 0;

        const totalRate =
          lmdValues.manual_total_cost > 0
            ? lmdValues.manual_total_cost
            : lmdValues.manual_base_rate + dieselSurcharge;

        const result: LmdCalculation = {
          baseRate: lmdValues.manual_base_rate,
          dieselSurcharge,
          totalRate,
          palletCount: lmdValues.manual_pallets,
          description: `LMD ${lmdValues.country} (${
            lmdValues.manual_pallets
          } pallet${lmdValues.manual_pallets !== 1 ? "s" : ""})`,
        };

        setCalculation(result);
        setValidationErrors([]);
        setLoading(false);
        return;
      }

      // Auto calculation mode or carton shipment mode
      let totalCartons = getTotalCartonsFromBooking();
      let palletCount = 1;
      let forceCartonRate = false;

      if (lmdValues.delivery_type === "carton") {
        // Force carton rate and use custom carton count if specified
        forceCartonRate = true;
        palletCount = 1;
        if (lmdValues.carton_shipment_cartons > 0) {
          totalCartons = lmdValues.carton_shipment_cartons;
        }
      } else {
        // Auto mode - smart detection
        // Get dynamic settings
        const [cartonsPerPallet, palletThreshold] = await Promise.all([
          getCartonsPerPallet(),
          getPalletThreshold(),
        ]);

        if (totalCartons > palletThreshold) {
          palletCount = Math.ceil(totalCartons / cartonsPerPallet);
        } else {
          // For small shipments, use carton rate
          forceCartonRate = true;
          palletCount = 1;
        }
      }

      const result = await calculateLmdCost(
        lmdValues.country,
        palletCount,
        totalCartons,
        lmdValues.apply_diesel_surcharge,
        lmdValues.diesel_surcharge_percentage,
        forceCartonRate
      );

      setCalculation(result);
      setValidationErrors([]);
    } catch (error) {
      console.error("Error calculating LMD cost:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setValidationErrors([`Calculation failed: ${errorMsg}`]);
      message.error("Failed to calculate LMD cost");
      setCalculation(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle switching delivery type modes
  const handleDeliveryTypeChange = (value: "auto" | "manual" | "carton") => {
    if (value === "manual" && calculation) {
      // Preserve the current auto-calculated values when switching to manual
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
        manual_pallets: calculation.palletCount,
        manual_base_rate: Number(calculation.baseRate.toFixed(2)),
        manual_total_cost: Number(calculation.totalRate.toFixed(2)),
      }));
    } else if (value === "carton") {
      // Initialize carton count when switching to carton mode
      const totalCartons = getTotalCartonsFromBooking();
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
        carton_shipment_cartons: totalCartons > 0 ? totalCartons : 1,
      }));
    } else {
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
      }));
    }
  };

  // Update calculated values when booking or settings change
  useEffect(() => {
    if (selectedBooking && availableCountries.length > 0) {
      performLmdCalculation();
    }
  }, [
    selectedBooking,
    lmdValues.delivery_type,
    lmdValues.apply_diesel_surcharge,
    lmdValues.diesel_surcharge_percentage,
    lmdValues.country,
    lmdValues.manual_pallets,
    lmdValues.manual_base_rate,
    lmdValues.manual_total_cost,
    lmdValues.carton_shipment_cartons,
    availableCountries,
  ]);

  // Add LMD to invoice lines
  const addLMDToInvoiceLines = () => {
    if (!calculation) {
      message.error("Please calculate LMD cost first");
      return;
    }

    if (validationErrors.length > 0) {
      message.error("Please fix validation errors first");
      return;
    }

    const lmdLine = {
      description: calculation.description,
      quantity: 1, // Always 1 for the total LMD cost
      unit_price: Number(calculation.totalRate.toFixed(2)), // Round to 2 decimals
      vat_percentage: lmdValues.vat_percentage,
      cost_type: "lmd_delivery",
    };

    onAddLMDLine(lmdLine);
    message.success("Last Mile Delivery cost added to invoice");

    // Reset calculation
    setCalculation(null);
  };

  if (!selectedBooking) {
    return null;
  }

  // If no delivery address and LMD override not shown, show info message
  if (!hasDeliveryAddress && !showLmdOverride) {
    return (
      <Card
        size="small"
        style={{
          marginBottom: 16,
          border: "2px solid #1890ff",
          backgroundColor: "#f0f9ff",
        }}
        title={
          <span style={{ color: "#1890ff", fontWeight: "bold" }}>
            📦 Storage Delivery (No LMD Required)
          </span>
        }
      >
        <Alert
          message="Booking will go to storage"
          description={
            <div>
              <p>
                This booking has no delivery address configured, so it will go
                straight to storage (no Last Mile Delivery needed).
              </p>
              <p>
                If you want to add LMD charges anyway, click the button below:
              </p>
            </div>
          }
          type="info"
          showIcon
          action={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowLmdOverride(true)}
            >
              Add LMD Override
            </Button>
          }
        />
      </Card>
    );
  }

  const totalCartons = getTotalCartonsFromBooking();

  // Prepare rate table data
  const rateTableData = availableRates.map((rate, index) => ({
    key: index,
    pallets: rate.pallets,
    rate: rate.rate_eur,
    description:
      rate.pallets === 0
        ? "Per carton"
        : `${rate.pallets} pallet${rate.pallets !== 1 ? "s" : ""}`,
    notes: rate.notes,
  }));

  const rateTableColumns = [
    {
      title: "Pallets",
      dataIndex: "pallets",
      key: "pallets",
      width: 80,
      render: (pallets: number) => (pallets === 0 ? "Carton" : pallets),
    },
    {
      title: "Rate (€)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (rate: number) => `€${rate}`,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        border: "2px solid #722ed1",
        backgroundColor: "#f9f0ff",
      }}
      title={
        <span style={{ color: "#722ed1", fontWeight: "bold" }}>
          🚚 Last Mile Delivery (LMD) Calculation
          {deliveryInfo && (
            <span
              style={{ fontSize: "12px", fontWeight: "normal", marginLeft: 8 }}
            >
              → {deliveryInfo.city}, {deliveryInfo.country}
            </span>
          )}
        </span>
      }
      extra={
        !hasDeliveryAddress && (
          <Button
            size="small"
            onClick={() => setShowLmdOverride(false)}
            type="text"
          >
            Hide Override
          </Button>
        )
      }
    >
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert
          message="Validation Issues"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}

      <Row gutter={8}>
        <Col span={24}>
          <div style={{ marginBottom: 12, fontSize: "12px" }}>
            <Text strong>Booking Details:</Text> {totalCartons} cartons total
            {hasDeliveryAddress && deliveryInfo && (
              <Text style={{ marginLeft: 8, color: "#52c41a" }}>
                ✓ Delivery to: {deliveryInfo.street_name}{" "}
                {deliveryInfo.house_number}, {deliveryInfo.city}
              </Text>
            )}
            {!hasDeliveryAddress && (
              <Text style={{ marginLeft: 8, color: "#fa8c16" }}>
                ⚠️ Override mode: No delivery address configured
              </Text>
            )}
            {lmdValues.delivery_type === "auto" && totalCartons > 7 && (
              <Text type="warning" style={{ marginLeft: 8 }}>
                (Auto-suggests pallet rate for {">"}7 cartons)
              </Text>
            )}
            {lmdValues.delivery_type === "carton" && (
              <Text style={{ marginLeft: 8, color: "#1890ff" }}>
                (Forcing carton rate: €
                {availableRates.find((r) => r.pallets === 0)?.rate_eur || 0} per
                carton)
              </Text>
            )}
          </div>
        </Col>
      </Row>

      <Row gutter={8}>
        <Col span={4}>
          <Form.Item label="Country" style={{ marginBottom: 8 }}>
            <Select
              value={lmdValues.country}
              onChange={(value) =>
                setLmdValues((prev) => ({ ...prev, country: value }))
              }
              size="small"
              loading={loading}
            >
              {availableCountries.map((country) => (
                <Option key={country} value={country}>
                  {country === "NL"
                    ? "🇳🇱 Netherlands"
                    : country === "BE"
                    ? "🇧🇪 Belgium"
                    : country}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item label="Calculation" style={{ marginBottom: 8 }}>
            <Select
              value={lmdValues.delivery_type}
              onChange={handleDeliveryTypeChange}
              size="small"
            >
              <Option value="auto">
                <Tooltip title="Automatically calculates based on carton count and database rates">
                  🤖 Auto Calculate
                </Tooltip>
              </Option>
              <Option value="carton">
                <Tooltip title="Forces carton rate regardless of quantity (per-carton pricing)">
                  📦 Carton Shipment
                </Tooltip>
              </Option>
              <Option value="manual">
                <Tooltip title="Manually specify all values - pallets, rates, total cost">
                  ✋ Manual Override
                </Tooltip>
              </Option>
            </Select>
          </Form.Item>
        </Col>

        {lmdValues.delivery_type === "carton" ? (
          <Col span={3}>
            <Form.Item label="Cartons" style={{ marginBottom: 8 }}>
              <InputNumber
                value={lmdValues.carton_shipment_cartons || totalCartons}
                onChange={(value) =>
                  setLmdValues((prev) => ({
                    ...prev,
                    carton_shipment_cartons: value || totalCartons,
                  }))
                }
                min={1}
                max={999}
                size="small"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        ) : (
          <Col span={3}>
            <Form.Item label="Pallets" style={{ marginBottom: 8 }}>
              <InputNumber
                value={
                  lmdValues.delivery_type === "auto"
                    ? calculation?.palletCount || 0
                    : lmdValues.manual_pallets
                }
                onChange={(value) => {
                  if (lmdValues.delivery_type === "manual") {
                    handleManualPalletChange(value || 1);
                  }
                }}
                readOnly={lmdValues.delivery_type === "auto"}
                min={1}
                max={50}
                size="small"
                style={{
                  width: "100%",
                  backgroundColor:
                    lmdValues.delivery_type === "manual" ? "white" : "#f5f5f5",
                }}
              />
            </Form.Item>
          </Col>
        )}

        <Col span={4}>
          <Form.Item label="Base Rate (€)" style={{ marginBottom: 8 }}>
            <InputNumber
              value={
                lmdValues.delivery_type === "auto" ||
                lmdValues.delivery_type === "carton"
                  ? calculation?.baseRate || 0
                  : lmdValues.manual_base_rate
              }
              onChange={(value) => {
                if (lmdValues.delivery_type === "manual") {
                  handleManualBaseRateChange(value || 0);
                }
              }}
              readOnly={
                lmdValues.delivery_type === "auto" ||
                lmdValues.delivery_type === "carton"
              }
              min={0}
              precision={2}
              size="small"
              style={{
                width: "100%",
                backgroundColor:
                  lmdValues.delivery_type === "manual" ? "white" : "#f5f5f5",
              }}
            />
          </Form.Item>
        </Col>

        <Col span={3}>
          <Form.Item
            label={
              <span>
                Diesel
                <Tooltip
                  title={`Apply ${lmdValues.diesel_surcharge_percentage}% diesel surcharge`}
                >
                  <InfoCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            style={{ marginBottom: 8 }}
          >
            <Space direction="vertical" size={0}>
              <Switch
                checked={lmdValues.apply_diesel_surcharge}
                onChange={(checked) =>
                  setLmdValues((prev) => ({
                    ...prev,
                    apply_diesel_surcharge: checked,
                  }))
                }
                size="small"
              />
              <Text style={{ fontSize: "11px" }}>
                {lmdValues.diesel_surcharge_percentage}%
              </Text>
            </Space>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item label="Total Cost (€)" style={{ marginBottom: 8 }}>
            <InputNumber
              value={
                lmdValues.delivery_type === "auto" ||
                lmdValues.delivery_type === "carton"
                  ? calculation?.totalRate || 0
                  : lmdValues.manual_total_cost
              }
              onChange={(value) => {
                if (lmdValues.delivery_type === "manual") {
                  setLmdValues((prev) => ({
                    ...prev,
                    manual_total_cost: value || 0,
                  }));
                }
              }}
              readOnly={
                lmdValues.delivery_type === "auto" ||
                lmdValues.delivery_type === "carton"
              }
              min={0}
              precision={2}
              size="small"
              style={{
                width: "100%",
                backgroundColor:
                  lmdValues.delivery_type === "manual" ? "white" : "#f5f5f5",
              }}
            />
          </Form.Item>
        </Col>

        <Col span={2}>
          <Form.Item label="Add" style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: "100%" }}
              onClick={addLMDToInvoiceLines}
              disabled={!calculation || loading || validationErrors.length > 0}
              loading={loading}
            >
              Add
            </Button>
          </Form.Item>
        </Col>
      </Row>

      {/* Calculation Breakdown */}
      {calculation && (
        <Row gutter={8} style={{ marginTop: 8 }}>
          <Col span={24}>
            <div
              style={{
                backgroundColor: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "12px",
              }}
            >
              <Text strong style={{ color: "#722ed1" }}>
                {lmdValues.delivery_type === "auto"
                  ? "Auto Calculation:"
                  : lmdValues.delivery_type === "carton"
                  ? "Carton Shipment:"
                  : "Manual Override:"}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text>Base Rate: €{calculation.baseRate.toFixed(2)}</Text>
                {calculation.dieselSurcharge > 0 && (
                  <Text style={{ marginLeft: 16 }}>
                    Diesel Surcharge: €{calculation.dieselSurcharge.toFixed(2)}
                    <Text type="secondary">
                      {" "}
                      ({lmdValues.diesel_surcharge_percentage}%)
                    </Text>
                  </Text>
                )}
                <Text strong style={{ marginLeft: 16, color: "#722ed1" }}>
                  Total: €{calculation.totalRate.toFixed(2)}
                </Text>
              </div>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{calculation.description}</Text>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Rate Preview Table - Show in Auto and Carton modes */}
      {(lmdValues.delivery_type === "auto" ||
        lmdValues.delivery_type === "carton") && (
        <Collapse size="small" style={{ marginTop: 12 }}>
          <Panel
            header={
              <span style={{ fontSize: "12px" }}>
                <EyeOutlined /> View Rate Table for {lmdValues.country} (
                {availableRates.length} rates available)
              </span>
            }
            key="rates"
          >
            <Table
              dataSource={rateTableData}
              columns={rateTableColumns}
              size="small"
              pagination={false}
              loading={ratesLoading}
              style={{ fontSize: "11px" }}
              scroll={{ y: 200 }}
            />
          </Panel>
        </Collapse>
      )}

      <Row gutter={8}>
        <Col span={24}>
          <div style={{ fontSize: "10px", color: "#666", marginTop: 8 }}>
            <Text>
              💡 <strong>Auto:</strong> Smart calculation (≤7 cartons = carton
              rate, {">"}7 cartons = pallet rate).
              <strong> Carton:</strong> Forces per-carton pricing regardless of
              quantity.
              <strong> Manual:</strong> Full override with auto-recalculation
              when pallets change.
              {(lmdValues.delivery_type === "auto" ||
                lmdValues.delivery_type === "carton") &&
                " Database rates: 1-15 pallets, diesel surcharge configurable."}
            </Text>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default LMDSection;
